// API Endpoints Configuration

import { config } from "./environment";

const API_BASE = `${config.api.baseUrl}/${config.api.version}`;

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: `${API_BASE}/auth/login`,
    LOGOUT: `${API_BASE}/auth/logout`,
    REFRESH: `${API_BASE}/auth/refresh`,
    FORGOT_PASSWORD: `${API_BASE}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE}/auth/reset-password`,
    CHANGE_PASSWORD: `${API_BASE}/auth/change-password`,
    VERIFY_TOKEN: `${API_BASE}/auth/verify`,
  },

  // Customers
  CUSTOMERS: {
    LIST: `${API_BASE}/customers`,
    CREATE: `${API_BASE}/customers`,
    GET: (id: string) => `${API_BASE}/customers/${id}`,
    UPDATE: (id: string) => `${API_BASE}/customers/${id}`,
    DELETE: (id: string) => `${API_BASE}/customers/${id}`,
    KYC: (id: string) => `${API_BASE}/customers/${id}/kyc`,
    ORDERS: (id: string) => `${API_BASE}/customers/${id}/orders`,
    PAYMENTS: (id: string) => `${API_BASE}/customers/${id}/payments`,
    STATISTICS: `${API_BASE}/customers/statistics`,
  },

  // Inventory
  INVENTORY: {
    LIST: `${API_BASE}/inventory`,
    CREATE: `${API_BASE}/inventory`,
    GET: (id: string) => `${API_BASE}/inventory/${id}`,
    UPDATE: (id: string) => `${API_BASE}/inventory/${id}`,
    DELETE: (id: string) => `${API_BASE}/inventory/${id}`,
    VARIANTS: (id: string) => `${API_BASE}/inventory/${id}/variants`,
    STOCK_LEVELS: `${API_BASE}/inventory/stock-levels`,
    LOW_STOCK: `${API_BASE}/inventory/low-stock`,
    CATEGORIES: `${API_BASE}/inventory/categories`,
  },

  // Orders
  ORDERS: {
    LIST: `${API_BASE}/orders`,
    CREATE: `${API_BASE}/orders`,
    GET: (id: string) => `${API_BASE}/orders/${id}`,
    UPDATE: (id: string) => `${API_BASE}/orders/${id}`,
    DELETE: (id: string) => `${API_BASE}/orders/${id}`,
    UPDATE_STATUS: (id: string) => `${API_BASE}/orders/${id}/status`,
    ADD_ITEMS: (id: string) => `${API_BASE}/orders/${id}/items`,
    DELIVERY: (id: string) => `${API_BASE}/orders/${id}/delivery`,
    EXPORT: `${API_BASE}/orders/export`,
    METRICS: `${API_BASE}/orders/metrics`,
  },

  // Suppliers
  SUPPLIERS: {
    LIST: `${API_BASE}/suppliers`,
    CREATE: `${API_BASE}/suppliers`,
    GET: (id: string) => `${API_BASE}/suppliers/${id}`,
    UPDATE: (id: string) => `${API_BASE}/suppliers/${id}`,
    DELETE: (id: string) => `${API_BASE}/suppliers/${id}`,
    KYC: (id: string) => `${API_BASE}/suppliers/${id}/kyc`,
    PERFORMANCE: (id: string) => `${API_BASE}/suppliers/${id}/performance`,
    STATISTICS: `${API_BASE}/suppliers/statistics`,
  },

  // Purchase Orders
  PURCHASE_ORDERS: {
    LIST: `${API_BASE}/purchase-orders`,
    CREATE: `${API_BASE}/purchase-orders`,
    GET: (id: string) => `${API_BASE}/purchase-orders/${id}`,
    UPDATE: (id: string) => `${API_BASE}/purchase-orders/${id}`,
    DELETE: (id: string) => `${API_BASE}/purchase-orders/${id}`,
    APPROVE: (id: string) => `${API_BASE}/purchase-orders/${id}/approve`,
    GRN: (id: string) => `${API_BASE}/purchase-orders/${id}/grn`,
    ITEMS: (id: string) => `${API_BASE}/purchase-orders/${id}/items`,
  },

  // Requisitions
  REQUISITIONS: {
    LIST: `${API_BASE}/requisitions`,
    CREATE: `${API_BASE}/requisitions`,
    GET: (id: string) => `${API_BASE}/requisitions/${id}`,
    UPDATE: (id: string) => `${API_BASE}/requisitions/${id}`,
    DELETE: (id: string) => `${API_BASE}/requisitions/${id}`,
    APPROVE: (id: string) => `${API_BASE}/requisitions/${id}/approve`,
    CONVERT_TO_PO: (id: string) =>
      `${API_BASE}/requisitions/${id}/convert-to-po`,
  },

  // Regions
  REGIONS: {
    LIST: `${API_BASE}/regions`,
    CREATE: `${API_BASE}/regions`,
    GET: (id: string) => `${API_BASE}/regions/${id}`,
    UPDATE: (id: string) => `${API_BASE}/regions/${id}`,
    DELETE: (id: string) => `${API_BASE}/regions/${id}`,
    OFFICERS: (id: string) => `${API_BASE}/regions/${id}/officers`,
    ADD_OFFICER: (id: string) => `${API_BASE}/regions/${id}/officers`,
    STATISTICS: (id: string) => `${API_BASE}/regions/${id}/statistics`,
  },

  // Users
  USERS: {
    LIST: `${API_BASE}/users`,
    CREATE: `${API_BASE}/users`,
    GET: (id: string) => `${API_BASE}/users/${id}`,
    UPDATE: (id: string) => `${API_BASE}/users/${id}`,
    DELETE: (id: string) => `${API_BASE}/users/${id}`,
    ROLES: `${API_BASE}/users/roles`,
    PERMISSIONS: `${API_BASE}/users/permissions`,
    PROFILE: `${API_BASE}/users/profile`,
  },

  // Transactions
  TRANSACTIONS: {
    LIST: `${API_BASE}/transactions`,
    CREATE: `${API_BASE}/transactions`,
    GET: (id: string) => `${API_BASE}/transactions/${id}`,
    UPDATE: (id: string) => `${API_BASE}/transactions/${id}`,
    PROCESS_PAYMENT: `${API_BASE}/transactions/process-payment`,
    REFUND: (id: string) => `${API_BASE}/transactions/${id}/refund`,
    REPORTS: `${API_BASE}/transactions/reports`,
  },

  // Promotions/Discounts
  PROMOTIONS: {
    LIST: `${API_BASE}/promotions`,
    CREATE: `${API_BASE}/promotions`,
    GET: (id: string) => `${API_BASE}/promotions/${id}`,
    UPDATE: (id: string) => `${API_BASE}/promotions/${id}`,
    DELETE: (id: string) => `${API_BASE}/promotions/${id}`,
    ACTIVATE: (id: string) => `${API_BASE}/promotions/${id}/activate`,
    DEACTIVATE: (id: string) => `${API_BASE}/promotions/${id}/deactivate`,
    ELIGIBILITY: (id: string) => `${API_BASE}/promotions/${id}/eligibility`,
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: `${API_BASE}/notifications`,
    GET: (id: string) => `${API_BASE}/notifications/${id}`,
    MARK_READ: (id: string) => `${API_BASE}/notifications/${id}/read`,
    MARK_ALL_READ: `${API_BASE}/notifications/mark-all-read`,
    DELETE: (id: string) => `${API_BASE}/notifications/${id}`,
    PREFERENCES: `${API_BASE}/notifications/preferences`,
  },

  // Dashboard
  DASHBOARD: {
    STATS: `${API_BASE}/dashboard/stats`,
    METRICS: `${API_BASE}/dashboard/metrics`,
    RECENT_ACTIVITY: `${API_BASE}/dashboard/recent-activity`,
    ALERTS: `${API_BASE}/dashboard/alerts`,
  },

  // Reports
  REPORTS: {
    SALES: `${API_BASE}/reports/sales`,
    INVENTORY: `${API_BASE}/reports/inventory`,
    CUSTOMERS: `${API_BASE}/reports/customers`,
    SUPPLIERS: `${API_BASE}/reports/suppliers`,
    FINANCIAL: `${API_BASE}/reports/financial`,
    EXPORT: `${API_BASE}/reports/export`,
  },

  // File Upload
  UPLOAD: {
    SINGLE: `${API_BASE}/upload/single`,
    MULTIPLE: `${API_BASE}/upload/multiple`,
    DOCUMENTS: `${API_BASE}/upload/documents`,
    IMAGES: `${API_BASE}/upload/images`,
  },

  // System Logs
  LOGS: {
    LIST: `${API_BASE}/logs`,
    GET: (id: string) => `${API_BASE}/logs/${id}`,
    AUDIT: `${API_BASE}/logs/audit`,
    ERROR: `${API_BASE}/logs/error`,
  },
} as const;

// Helper function to build URLs with query parameters
export function buildUrl(
  endpoint: string,
  params?: Record<string, any>
): string {
  if (!params || Object.keys(params).length === 0) {
    return endpoint;
  }

  const queryString = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

// Helper function to get paginated endpoint
export function getPaginatedUrl(
  endpoint: string,
  page: number = 1,
  limit: number = 10,
  filters?: Record<string, any>
): string {
  const params = {
    page,
    limit,
    ...filters,
  };
  return buildUrl(endpoint, params);
}

export default API_ENDPOINTS;
